import React, { useState, useMemo } from 'react';
import LapList from './LapList';
import { useStopwatchContext } from '../contexts';
import { convertPace } from '../utils/paceCalculation';
import { SegmentsByLap } from '../types';

// Extended lap interface that includes totalTime property needed in this component
interface ExtendedLap {
  id?: string;
  number: number;
  time: number;
  cumulativeTime?: number;
  timestamp?: string | number;
  isActive?: boolean;
  isFinalLap?: boolean;
  pace?: number | null;
}

interface ProcessedSegment {
  distance: number;
  time: number;
  pace: number | null;
  id?: string;
  lapNumber?: number;
  timestamp?: number;
}

interface ProcessedLap {
  lapNumber: number;
  lapTime: number;
  splitTime: number;
  pace: number | null;
  segments: ProcessedSegment[];
  totalDistance: number;
}

interface LapDisplayProps {
  laps: ExtendedLap[];
  effectiveSegmentsByLap?: SegmentsByLap; // For live view during workout
  sessionSegments?: SegmentsByLap; // For completed/review view
  calculatePace: (time: number, distance: number, unit: string) => number | null;
  formatPace: (pace: number, unit: string) => string;
}

/**
 * LapDisplay component to show lap times and segments
 */
const LapDisplay: React.FC<LapDisplayProps> = ({
  laps,
  effectiveSegmentsByLap = {},
  sessionSegments = {}, // Add default empty object
  calculatePace,
  formatPace,
}) => {
  const [showAllSegments, setShowAllSegments] = useState<boolean>(false);
  const { paceUnit, isRunning } = useStopwatchContext(); // <-- Get isRunning state

  // Toggle showing all segments
  const toggleShowAllSegments = (): void => {
    setShowAllSegments(!showAllSegments);
  };

  // Determine which segment data to use based on whether the workout is running
  const segmentsToUse = useMemo(() => {
    // If the timer is running, use the live effectiveSegmentsByLap
    // Otherwise, use the segments from the completed session
    // BUT if sessionSegments is empty, fall back to effectiveSegmentsByLap
    // This helps when transitioning from running to completed state
    if (isRunning) {
      return effectiveSegmentsByLap;
    } else {
      // Create a merged segments object that prioritizes sessionSegments
      // but falls back to effectiveSegmentsByLap when needed
      const mergedSegments: SegmentsByLap = {};

      // Get all potential lap numbers
      const allLapNumbers = new Set([
        ...Object.keys(sessionSegments || {}).map(Number),
        ...Object.keys(effectiveSegmentsByLap || {}).map(Number),
      ]);

      // For each lap number, use sessionSegments if it exists, otherwise use effectiveSegmentsByLap
      Array.from(allLapNumbers).forEach(lapNumber => {
        const sessionLapSegments = sessionSegments?.[lapNumber] || [];
        const effectiveLapSegments = effectiveSegmentsByLap?.[lapNumber] || [];

        // Always use the non-empty segment array, prioritizing session segments if both exist
        mergedSegments[lapNumber] =
          sessionLapSegments.length > 0 ? sessionLapSegments : effectiveLapSegments;
      });

      return mergedSegments;
    }
  }, [isRunning, effectiveSegmentsByLap, sessionSegments]);

  // Get lap and segment data
  const lapData = useMemo<ProcessedLap[]>(() => {
    // Use the determined segmentsToUse
    const currentSegments = segmentsToUse || {};

    return laps.map((lap, index) => {
      const splitTime = lap.cumulativeTime || 0;
      const lapTime = lap.time;

      // Get segments for this lap using the appropriate source
      const segments = currentSegments[index + 1] || []; // Use currentSegments
      const totalLapDistance = segments.reduce((sum, segment) => sum + segment.distance, 0);

      // Whether the lap was completed using the lap button (should not show pace)
      // or using distance button with auto-lap (should show pace)
      const wasLapEndedWithLapButton = lap.pace === undefined || lap.pace === null;

      // Get pace value and handle conversion
      let pace: number | null = null;

      if (!wasLapEndedWithLapButton) {
        // If lap has raw time and distance data, calculate pace with current unit directly
        if (totalLapDistance > 0) {
          pace = calculatePace(lapTime, totalLapDistance, paceUnit);
        }
        // If lap only has a stored pace (from workout history), convert it from mile to the current unit
        else if (lap.pace !== undefined && lap.pace !== null && isFinite(lap.pace)) {
          const sourcePaceUnit = 'mile'; // Assume stored paces are in min/mile
          pace =
            paceUnit !== sourcePaceUnit
              ? convertPace(lap.pace, sourcePaceUnit, paceUnit)
              : lap.pace;
        }
      }

      const processedSegments = segments.map(segment => {
        // If segment has raw time and distance data, recalculate the pace with the current unit
        if (segment.time > 0 && segment.distance > 0) {
          return {
            ...segment,
            pace: calculatePace(segment.time, segment.distance, paceUnit),
          };
        }
        // If segment only has a pace value but no raw data, convert the pace directly
        else if (segment.pace) {
          // We don't know the original unit, but segments are stored in minutes/mile by default
          // so convert from that if we're displaying km
          const sourcePaceUnit = 'mile'; // Assuming segments are stored with pace in min/mile
          if (paceUnit !== sourcePaceUnit) {
            return {
              ...segment,
              pace: convertPace(segment.pace, sourcePaceUnit, paceUnit),
            };
          }
        }
        return segment;
      });

      return {
        lapNumber: index + 1,
        lapTime,
        splitTime,
        pace,
        segments: processedSegments,
        totalDistance: totalLapDistance,
      };
    });
  }, [laps, segmentsToUse, calculatePace, paceUnit]);

  // Handle if we're still on the first lap with no completed laps
  const showEmptyState = lapData.length === 0;

  return (
    <div className="lap-display">
      <h3 className="lap-display__header">Lap History</h3>

      {showEmptyState ? (
        <div className="lap-display__empty">No laps recorded yet</div>
      ) : (
        <>
          <LapList
            lapData={lapData}
            showAllSegments={showAllSegments}
            formatPace={formatPace}
            onToggleSegments={toggleShowAllSegments}
            paceUnit={paceUnit}
          />
        </>
      )}
    </div>
  );
};

export default LapDisplay;
